import { endPoint } from '@/constant/end-point'
import type { FontSize } from '@/contexts/accessibility.context'
import type { Theme } from '@/contexts/theme.context'
import { UserPreferenceContext } from '@/contexts/user-preference-context'
import { useAccessibility } from '@/hooks/use-accessibility'
import useAPI from '@/hooks/use-api'
import useApiMutation from '@/hooks/use-api-mutation'
import { useTheme } from '@/hooks/use-theme'
import type { ICreateUserPreference, IUserPreference } from '@/interface/user-preference.interface'
import { FONT_SIZE_PREFERENCE_KEY, HIGH_CONTRAST_PREFERENCE_KEY } from '@/providers/accessibility.provider'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'

interface UserPreferenceProviderProps {
  children: React.ReactNode
}

export const UserPreferenceProvider: React.FC<UserPreferenceProviderProps> = ({ children }) => {
  const { get } = useAPI<IUserPreference>()
  const { setTheme } = useTheme()
  const { setFontSize, setHighContrastMode } = useAccessibility()

  const { createMutation } = useApiMutation<ICreateUserPreference>({
    endpoint: `${endPoint.userPreference}`,
    customQueryKey: ['user-preferences'],
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => get(endPoint.userPreference),
  })

  const { mutateAsync: upsertPreference } = createMutation

  const upsertUserPreference = React.useCallback(
    async (key: string, value: string, active: boolean = true) => {
      await upsertPreference({
        key,
        value,
        active,
      })
    },
    [upsertPreference]
  )

  // Sync theme from backend preferences
  React.useEffect(() => {
    if (data?.data) {
      const preferences = data.data as unknown as IUserPreference[]

      // Apply theme
      const theme = preferences.find((preference) => preference.key === 'theme')
      if (theme) setTheme(theme.value as Theme)

      // Apply font size
      const fontSizePref = preferences.find((preference) => preference.key === FONT_SIZE_PREFERENCE_KEY)
      if (fontSizePref && ['small', 'medium', 'large', 'extra-large'].includes(fontSizePref.value))
        setFontSize(fontSizePref.value as FontSize)

      // Apply high contrast mode
      const highContrastPref = preferences.find((preference) => preference.key === HIGH_CONTRAST_PREFERENCE_KEY)
      if (highContrastPref) setHighContrastMode(highContrastPref.value === 'on')
    }
  }, [data?.data, setTheme, setFontSize, setHighContrastMode])

  const contextValue = React.useMemo(
    () => ({
      userPreferences: data?.data as unknown as IUserPreference[],
      isLoading,
      error,
      upsertUserPreference,
    }),
    [data?.data, isLoading, error, upsertUserPreference]
  )

  return <UserPreferenceContext.Provider value={contextValue}>{children}</UserPreferenceContext.Provider>
}
